import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { BrandSettingsProvider } from "./context/BrandSettingsContext";
import HomePage from "./pages/HomePage";
import ResultsPage from "./pages/ResultsPage";
import SharedResultPage from "./pages/SharedResultPage";
import HistoryPage from "./pages/HistoryPage";
import PricingPage from "./pages/PricingPage";
import ProfilePage from "./pages/ProfilePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import TermsPage from "./pages/TermsPage";
import AdminSourcesPage from "./pages/AdminSourcesPage";
import AdminPromptsPage from "./pages/AdminPromptsPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminFactCheckersPage from "./pages/AdminFactCheckersPage";
import AdminDirectoryPage from "./pages/AdminDirectoryPage";
import AdminComplaintsPage from "./pages/AdminComplaintsPage";
import AdminSetupPage from "./pages/AdminSetupPage";
import AdminMaintenancePage from "./pages/AdminMaintenancePage";
import AdminBrandSettingsPage from "./pages/AdminBrandSettingsPage";
import AdminPaymentSettingsPage from "./pages/AdminPaymentSettingsPage";
import InfluencerCertificationPage from "./pages/InfluencerCertificationPage";
import DirectoryPage from "./pages/DirectoryPage";
import AboutPage from "./pages/AboutPage";
import SharePage from "./pages/SharePage";
import TransparencyPage from "./pages/TransparencyPage";
import CorrectionsPage from "./pages/CorrectionsPage";
import MethodologyPage from "./pages/MethodologyPage";
import FundingPage from "./pages/FundingPage";
import IndependencePage from "./pages/IndependencePage";
import UpgradeSuccessPage from "./pages/UpgradeSuccessPage";
import UpgradeCancelPage from "./pages/UpgradeCancelPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import FloatingWidget from "./components/FloatingWidget";
import CookieConsent from "./components/CookieConsent";
import AuthCallback from "./components/AuthCallback";
import MaintenanceBanner from "./components/MaintenanceBanner";
import { Toaster } from "./components/ui/sonner";

/**
 * AppRouter handles routing and OAuth callback detection.
 * CRITICAL: session_id check must happen synchronously during render,
 * NOT in useEffect, to prevent race conditions.
 */
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment (not query params) for session_id - SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute checks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <MaintenanceBanner />
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/results" element={<ResultsPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/result/:factCheckId" element={<SharedResultPage />} />
        <Route path="/admin/sources" element={<AdminSourcesPage />} />
        <Route path="/admin/prompts" element={<AdminPromptsPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/factcheckers" element={<AdminFactCheckersPage />} />
        <Route path="/admin/directory" element={<AdminDirectoryPage />} />
        <Route path="/admin/complaints" element={<AdminComplaintsPage />} />
        <Route path="/admin/setup" element={<AdminSetupPage />} />
        <Route path="/admin/maintenance" element={<AdminMaintenancePage />} />
        <Route path="/admin/brand-settings" element={<AdminBrandSettingsPage />} />
        <Route path="/admin/payment-settings" element={<AdminPaymentSettingsPage />} />
        <Route path="/influencer" element={<InfluencerCertificationPage />} />
        <Route path="/directory" element={<DirectoryPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/share" element={<SharePage />} />
        <Route path="/transparency" element={<TransparencyPage />} />
        <Route path="/transparency/corrections" element={<CorrectionsPage />} />
        <Route path="/transparency/methodology" element={<MethodologyPage />} />
        <Route path="/transparency/funding" element={<FundingPage />} />
        <Route path="/transparency/independence" element={<IndependencePage />} />
        <Route path="/upgrade/success" element={<UpgradeSuccessPage />} />
        <Route path="/upgrade/cancel" element={<UpgradeCancelPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Routes>
      <FloatingWidget />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrandSettingsProvider>
        <div className="App min-h-screen bg-background text-foreground transition-colors duration-300">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </div>
      </BrandSettingsProvider>
    </ThemeProvider>
  );
}

export default App;
